/**
 * The offline write queue.
 *
 * People log food in kitchens, gyms, basements and restaurants, and an entry
 * that silently fails to save is the worst bug this app can have — worse than
 * a crash, because a crash is visible. The server half of this has been done
 * since the schema was written: `food_logs` is unique on
 * `(identity_id, client_id)`, so replaying a write is idempotent, and
 * `GET /v1/logs?since=` returns tombstones for deletes. What was missing is the
 * client half — persisting writes and replaying them.
 *
 * This module is the part worth testing rather than trusting: what goes in the
 * queue, what collapses, what order it replays in, and what is dropped rather
 * than retried forever. It touches no storage and no React; the app supplies a
 * `QueueStore` and drives it.
 *
 * ## Identity while offline
 *
 * An entry created offline has no server id, so the client gives it one: its
 * own `client_id`. That is what makes "log a meal, then fix its weight, then
 * delete it, all on a train" expressible — every later operation names the same
 * id, and `drainQueue` swaps in the real one the moment the create lands.
 */

import { ApiError } from "@calorio/api-client";

import type { Macros } from "./macros";
import { type Nutrients, pickNutrients } from "./nutrients";
import type { ApiClient } from "./scan";
import type { FoodLog, LogInput, Meal } from "./types";

/** The body `POST /v1/logs` takes, with the device-minted id already in it. */
export interface QueuedLogBody extends LogInput {
  client_id: string;
}

/**
 * The body `POST /v1/batches/{id}/log` takes.
 *
 * Its own shape rather than a `QueuedLogBody` with a batch id bolted on,
 * because the endpoint is not a log endpoint that happens to know about pots:
 * it writes the entry *and* takes the portion out of the fridge, and the two
 * have to be one request or a phone that loses signal between them empties a
 * dish nobody ate.
 */
export interface QueuedPortionBody {
  client_id: string;
  meal?: Meal;
  /**
   * How much, said one way or the other — never both, which the server
   * refuses. `portions` is a multiple of one portion; `quantity_g` is the
   * plate on the scales, and only makes sense for a pot that was weighed.
   * Neither is one portion, which is what every write queued by an older
   * build says.
   */
  portions?: number;
  quantity_g?: number;
  logged_at?: string;
}

export interface QueuedQuickBody {
  client_id: string;
  name: string;
  kcal: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  meal?: Meal;
  logged_at?: string;
}

/**
 * One write, as it waits.
 *
 * A create also carries the row the diary is showing for it (`optimistic`).
 * The body cannot stand in for it: `POST /v1/logs` takes a food id and a
 * weight, and a diary row needs a name and four macro figures, which only the
 * screen that made the write knew. Carrying it means the queue is enough on its
 * own to say what the day looks like — see `applyQueued` — including after a
 * relaunch, since the queue is persisted and the row goes with it.
 *
 * Optional because a queue written by an older build has none. Such a write
 * still sends; it is only invisible until it does.
 */
export type QueuedWrite =
  | { op: "log"; id: string; body: QueuedLogBody; optimistic?: FoodLog }
  | { op: "quick"; id: string; body: QueuedQuickBody; optimistic?: FoodLog }
  /**
   * Eat one out of a pot. A create like the two above — it writes a diary row —
   * that also spends a portion, which is why it cannot be expressed as a plain
   * `log` against the pot's food.
   *
   * Safe to replay for the same reason every other write here is: the server
   * decrements only when the `client_id` is one it has not seen, so a retry in
   * a tunnel re-lands the entry and leaves the count alone.
   */
  | {
      op: "portion";
      id: string;
      batchId: string;
      body: QueuedPortionBody;
      optimistic?: FoodLog;
      /**
       * The portions the screen took off the pot when this was queued, so an
       * undo before it drains can put back exactly that. Derived from the
       * body at write time — a grams body has no portion count of its own.
       * Optional because a queue written before it existed carries only
       * `body.portions`, and that write still means what it meant.
       */
      spent?: number;
    }
  | { op: "update"; id: string; changes: Partial<FoodLog> }
  | { op: "delete"; id: string };

/** Where the queue lives between launches. Supplied by the app. */
export interface QueueStore {
  load(): Promise<QueuedWrite[]>;
  save(queue: readonly QueuedWrite[]): Promise<void>;
}

const isCreate = (write: QueuedWrite) =>
  write.op === "log" || write.op === "quick" || write.op === "portion";

/**
 * Add a write, collapsing what the queue can answer without the network.
 *
 * Three rules, each of which exists because the naive queue gets it wrong:
 *
 * - **Deleting something still queued removes it.** Otherwise a meal logged and
 *   undone on the same train ride replays as a create followed by a delete —
 *   two round trips to reach the state of having done nothing, and a row that
 *   briefly exists on every other device.
 * - **Updates to the same entry merge.** Nudging a weight four times should
 *   send one PATCH, not four, and PATCH bodies merge exactly because each names
 *   only the fields it changes.
 * - **A delete supersedes the updates before it.** Sending edits to a row you
 *   are about to remove is work whose only possible outcome is a 404 race.
 *
 * Returns a new array; the caller persists it.
 */
export function enqueue(queue: readonly QueuedWrite[], write: QueuedWrite): QueuedWrite[] {
  if (write.op === "delete") {
    const pendingCreate = queue.some((q) => isCreate(q) && q.id === write.id);
    const withoutTarget = queue.filter((q) => q.id !== write.id);
    // A create that never reached the server needs no tombstone: nothing else
    // has ever seen the row.
    return pendingCreate ? withoutTarget : [...withoutTarget, write];
  }

  if (write.op === "update") {
    const existing = queue.findIndex((q) => q.op === "update" && q.id === write.id);
    if (existing >= 0) {
      const merged = queue.slice();
      const current = merged[existing] as Extract<QueuedWrite, { op: "update" }>;
      merged[existing] = { ...current, changes: { ...current.changes, ...write.changes } };
      return merged;
    }
    return [...queue, write];
  }

  return [...queue, write];
}

/**
 * A failure the server will give again however many times we ask.
 *
 * The distinction is the difference between a queue that drains and one that
 * never empties. A 422 from a body this version of the app can no longer build
 * is not going to start succeeding, and retrying it forever blocks every write
 * behind it — the queue is ordered, so one poisoned entry stalls the lot.
 *
 * 408 and 429 are timing, not judgement, so they stay retryable. A 401 is
 * excluded too: the client refreshes and retries on its own, and a genuinely
 * dead session should stall the queue until the user signs in rather than
 * discard their food diary.
 */
function isPermanent(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const { status } = error;
  if (status === 401 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}

export interface DrainResult {
  /** What is still waiting, in order. Persist this. */
  remaining: QueuedWrite[];
  /** How many reached the server. */
  sent: number;
  /** Refused outright, and discarded rather than retried forever. */
  dropped: { write: QueuedWrite; reason: string }[];
  /** Local id → server id, for creates that landed during this drain. */
  resolved: Record<string, string>;
}

/**
 * Replay the queue, in order, until it empties or the network stops answering.
 *
 * Order is not negotiable: an update must not overtake the create it edits, and
 * two edits to one entry must land in the order they were made. So this stops
 * at the first transient failure rather than skipping past it — a queue that
 * reorders itself under a flaky connection produces a diary nobody can explain.
 */
export async function drainQueue(
  api: ApiClient,
  queue: readonly QueuedWrite[],
  /**
   * Local ids already resolved by an earlier drain.
   *
   * Carried across calls because a create can land in one drain and the delete
   * for it be made in the next — by which point the only id the device has for
   * that row is still the local one.
   */
  known: Readonly<Record<string, string>> = {},
): Promise<DrainResult> {
  const resolved: Record<string, string> = { ...known };
  const dropped: DrainResult["dropped"] = [];
  let sent = 0;

  for (let index = 0; index < queue.length; index += 1) {
    const write = queue[index]!;
    // An entry created offline was addressed by its client id. Once the create
    // has landed, everything after it must name the row the server made.
    const target = resolved[write.id] ?? write.id;

    try {
      if (write.op === "log") {
        const entry = await api.post<FoodLog>("/v1/logs", write.body);
        resolved[write.id] = entry.id;
      } else if (write.op === "quick") {
        const entry = await api.post<FoodLog>("/v1/logs/quick", write.body);
        resolved[write.id] = entry.id;
      } else if (write.op === "portion") {
        const result = await api.post<{ log: FoodLog }>(
          `/v1/batches/${write.batchId}/log`,
          write.body,
        );
        resolved[write.id] = result.log.id;
      } else if (write.op === "update") {
        await api.patch<FoodLog>(`/v1/logs/${target}`, write.changes);
      } else {
        await api.delete<void>(`/v1/logs/${target}`);
      }
      sent += 1;
    } catch (error) {
      if (isPermanent(error)) {
        dropped.push({ write, reason: error instanceof Error ? error.message : "refused" });
        continue;
      }
      return { remaining: queue.slice(index), sent, dropped, resolved };
    }
  }

  return { remaining: [], sent, dropped, resolved };
}

/**
 * The optimistic row a queued create should show in the diary.
 *
 * Built from what the device already knows rather than waiting for the server
 * to compute it, so the entry appears the instant it is logged and does not
 * visibly change when the queue drains.
 *
 * `macros` are absolute, not per-100. The caller scales them (`forGrams` for a
 * food, the figures as typed for a quick add) because a quick add has no weight
 * at all — its column is NOT NULL so it stores zero — and a signature that took
 * per-100 values would force the caller to invent a divisor for it.
 *
 * `nutrients` are absolute for the same reason, and default to all-unknown.
 * That default is what a quick add wants: it knows the calories and nothing
 * else, so it belongs in the denominator of "6 of 9" and out of the numerator.
 */
export function optimisticEntry(input: {
  clientId: string;
  localDate: string;
  name: string;
  meal: Meal;
  quantityG: number;
  macros: Macros;
  nutrients?: Partial<Nutrients>;
  foodItemId: string | null;
  loggedAt?: string;
}): FoodLog {
  const now = input.loggedAt ?? new Date().toISOString();
  return {
    // The client id doubles as the row id until the server issues one. That is
    // what lets an edit or a delete made before the queue drains name the same
    // entry.
    id: input.clientId,
    client_id: input.clientId,
    food_item_id: input.foodItemId,
    name: input.name,
    quantity_g: input.quantityG,
    serving_label: null,
    kcal: input.macros.kcal,
    protein_g: input.macros.protein_g,
    carbs_g: input.macros.carbs_g,
    fat_g: input.macros.fat_g,
    ...pickNutrients(input.nutrients),
    meal: input.meal,
    logged_at: now,
    local_date: input.localDate,
    updated_at: now,
    deleted_at: null,
  };
}

/** Both ids a queued write might be addressing this row by. */
const names = (row: FoodLog, id: string) => row.id === id || row.client_id === id;

/**
 * A day as it stands once the queue is taken into account.
 *
 * `patchDay` writes an optimistic row into the cached day at the moment of the
 * write, which is enough right up until that day is fetched again — and it is
 * fetched again often, because a day is stale the instant it is cached
 * (`staleTime.day` is 0) and every tab that regains focus refetches. The server
 * has not seen the queued writes yet, so its answer is correct and does not
 * contain them, and the entry the user is looking at disappears. It is still
 * queued and it does still send, but a diary that drops the food you just
 * logged is indistinguishable from one that lost it, and this app's stated
 * worst bug is exactly that.
 *
 * So the fetched day is not the day: the day is the fetched day with the queue
 * applied over it, replayed in the order it will be sent.
 *
 * Two things make replaying safe to do repeatedly:
 *
 * - **A create already answered for is skipped.** A fetch that started before a
 *   write landed comes back without it; one that started after comes back with
 *   it, under the server's id, while the write itself is still in the queue
 *   being sent. `client_id` is the same on both sides, which is what tells the
 *   two apart — without it, the second case counts the meal twice.
 * - **Updates and deletes are idempotent.** Applying an edit the server has
 *   already made changes nothing, and removing a row that is already gone is a
 *   filter that matches nothing.
 *
 * Returns `entries` itself when the queue has nothing to say about this day, so
 * a caller can tell "unchanged" without comparing.
 */
export function applyQueued(
  entries: FoodLog[],
  day: string,
  queue: readonly QueuedWrite[],
): FoodLog[] {
  let rows = entries;
  let changed = false;

  for (const write of queue) {
    if (isCreate(write)) {
      const row = write.optimistic;
      if (!row || row.local_date !== day) continue;
      if (rows.some((entry) => names(entry, write.id))) continue;
      // Appended, where `patchDay` appends it, so a refetch does not shuffle
      // the entry to a different place in its meal than the one it appeared in.
      rows = [...rows, row];
    } else if (!rows.some((entry) => names(entry, write.id))) {
      continue;
    } else if (write.op === "update") {
      rows = rows.map((entry) =>
        names(entry, write.id) ? { ...entry, ...write.changes } : entry,
      );
    } else {
      rows = rows.filter((entry) => !names(entry, write.id));
    }
    changed = true;
  }

  if (!changed) return entries;
  // A pending edit can move an entry to another day, which on this day means it
  // is not here any more.
  return rows.every((entry) => entry.local_date === day)
    ? rows
    : rows.filter((entry) => entry.local_date === day);
}
